<script setup>
import { computed, h } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';

import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';

const { inboxMetrics, isLoading, pageIndex, inboxTotalCount } = defineProps({
  inboxMetrics: {
    type: Array,
    default: () => [],
  },
  inboxTotalCount: {
    type: Number,
    default: 0,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  pageIndex: {
    type: Number,
    default: 1,
  },
});

const emit = defineEmits(['pageChange']);
const { t } = useI18n();

// const totalCount = computed(() => inboxMetrics.length);

const tableData = computed(() => {
  return inboxMetrics
    .map(inboxItem => {
      const { metric: _metric } = inboxItem ?? {};
      const metric = _metric ?? {};
      return {
        name: inboxItem.name,
        conversations: metric.conversations, // 接收会话数 100
        botResolved: metric.botResolved, // bot已解决会话数 15
        agentResolved: metric.agentResolved, // 人工已解决会话数 15
        pending: metric.pending, // pending会话数 3
        open: metric.open, // open会话数 2
        onHold: metric.onHold || 0,
        avgFirstResponseTime: metric.avgFirstResponseTime, // 平均首次响应时间 "10min"
        avgResolveTime: metric.avgResolveTime, // 平均解决时间 "10min"
        csat: metric.csat, // CSAT 4.5
      };
    });
});

const defaulSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue() ? 'whitespace-normal break-words' : 'text-slate-300 dark:text-slate-700',
    },
    cellProps.getValue() ? cellProps.getValue() : '---'
  );

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('name', {
    header: t('OVERVIEW_REPORTS.INBOX_CONVERSATIONS.TABLE_HEADER.NAME'),
    cell: defaulSpanRender,
    size: 260,
  }),
  columnHelper.accessor('conversations', {
    header: t('OVERVIEW_REPORTS.INBOX_CONVERSATIONS.TABLE_HEADER.CONVERSATIONS'),
    cell: defaulSpanRender,
    size: 140,
  }),
  columnHelper.accessor('botResolved', {
    header: t('OVERVIEW_REPORTS.INBOX_CONVERSATIONS.TABLE_HEADER.BOT_RESOLVED'),
    cell: defaulSpanRender,
    size: 130,
  }),
  
  columnHelper.accessor('agentResolved', {
    header: t('OVERVIEW_REPORTS.INBOX_CONVERSATIONS.TABLE_HEADER.AGENT_RESOLVED'),
    cell: defaulSpanRender,
    size: 140,
  }),
  
  columnHelper.accessor('pending', {
    header: t('OVERVIEW_REPORTS.INBOX_CONVERSATIONS.TABLE_HEADER.PENDING'),
    cell: defaulSpanRender,
    size: 95,
  }),
  
  columnHelper.accessor('open', {
    header: t('OVERVIEW_REPORTS.INBOX_CONVERSATIONS.TABLE_HEADER.OPEN'),
    cell: defaulSpanRender,
    size: 80,
  }),

  columnHelper.accessor('onHold', {
    header: 'On Hold',
    cell: defaulSpanRender,
    size: 100,
  }),
  
  columnHelper.accessor('avgFirstResponseTime', {
    header: t('OVERVIEW_REPORTS.INBOX_CONVERSATIONS.TABLE_HEADER.AVG_FIRST_RESPONSE_TIME'),
    cell: defaulSpanRender,
    size: 230,
  }),
  
  columnHelper.accessor('avgResolveTime', {
    header: t('OVERVIEW_REPORTS.INBOX_CONVERSATIONS.TABLE_HEADER.AVG_RESOLVE_TIME'),
    cell: defaulSpanRender,
    size: 180,
  }),
  
  columnHelper.accessor('csat', {
    header: t('OVERVIEW_REPORTS.INBOX_CONVERSATIONS.TABLE_HEADER.CSAT'),
    cell: defaulSpanRender,
    size: 80,
  }),
];

const paginationParams = computed(() => {
  return {
    pageIndex: pageIndex,
    pageSize: 25,
  };
});

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return inboxTotalCount;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagintaion = updater(paginationParams.value);
    emit('pageChange', newPagintaion.pageIndex);
  },
});
</script>

<template>
  <div class="inbox-table-container">
    <div class="overflow-x-auto shadow-[inset_-8px_0_8px_-4px_#ddd]">
      <Table fixed :table="table" class="max-h-[calc(100vh-21.875rem)]" />
    </div>
    <Pagination class="mt-2" :table="table" />
    <div v-if="isLoading" class="agents-loader">
      <Spinner />
      <span>{{
        $t('OVERVIEW_REPORTS.INBOX_CONVERSATIONS.LOADING_MESSAGE')
      }}</span>
    </div>
    <EmptyState
      v-else-if="!isLoading && !inboxMetrics.length"
      :title="$t('OVERVIEW_REPORTS.INBOX_CONVERSATIONS.NO_INBOXES')"
    />
  </div>
</template>

<style lang="scss" scoped>
.inbox-table-container {
  @apply flex flex-col flex-1 w-full;

  .ve-table {
    &::v-deep {
      th.ve-table-header-th {
        @apply text-sm rounded-xl;
        padding: var(--space-small) var(--space-two) !important;
      }

      td.ve-table-body-td {
        padding: var(--space-one) var(--space-two) !important;
      }
    }
  }

  &::v-deep .ve-pagination {
    @apply bg-transparent dark:bg-transparent;
  }

  &::v-deep .ve-pagination-select {
    @apply hidden;
  }

  .row-user-block {
    @apply items-center flex text-left;

    .user-block {
      @apply items-start flex flex-col min-w-0 my-0 mx-2;

      .title {
        @apply text-sm m-0 leading-[1.2] text-slate-800 dark:text-slate-100;
      }

      .sub-title {
        @apply text-xs text-slate-600 dark:text-slate-200;
      }
    }
  }

  .table-pagination {
    @apply mt-4 text-right;
  }
}

.agents-loader {
  @apply items-center flex text-base justify-center p-8;
}
</style>
